import React, { useEffect, useRef, useState } from 'react';
import ModalOverlay from './shared/ModalOverlay';
import ModalPanel, { ModalCloseButton } from './shared/ModalPanel';
import {
  MODAL_SECTIONS,
  getModalSectionTheme,
  modalButtonClass,
  resolveModalSection,
} from './shared/modalTheme';
import { ModalSectionProvider, useModalSection } from './shared/ModalSectionContext';

export { MODAL_SECTIONS, getModalSectionTheme, resolveModalSection, useModalSection };
export { default as ThemedDialog } from './shared/ThemedDialog';
export { default as ModalPanel } from './shared/ModalPanel';

/** @deprecated Use modalButtonClass() — kept for imports that read ButtonStyles keys */
export const ButtonStyles = {
  primary: modalButtonClass('primary', 'md'),
  success: modalButtonClass('pdf', 'md'),
  danger: modalButtonClass('danger', 'md'),
  secondary: modalButtonClass('secondary', 'md'),
  light: modalButtonClass('light', 'sm'),
  info: modalButtonClass('primary', 'md'),
  warning: modalButtonClass('warning', 'md'),
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-sm sm:text-base',
};

const VARIANT_ALIAS = { success: 'pdf', info: 'primary' };

export const StandardButton = ({
  children,
  variant = 'primary',
  size = 'md',
  onClick,
  disabled = false,
  className = '',
  type = 'button',
  section: sectionProp,
}) => {
  const sectionCtx = useModalSection();
  const section = sectionProp != null ? resolveModalSection(sectionProp) : sectionCtx;
  const resolved = VARIANT_ALIAS[variant] || variant;
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${modalButtonClass(resolved, size, '', section)} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
    >
      {children}
    </button>
  );
};

export const ActionBar = ({ children, className = '', justify = 'between' }) => {
  const justifyClasses = {
    between: 'justify-between',
    start: 'justify-start',
    end: 'justify-end',
    center: 'justify-center',
  };

  return (
    <div
      className={`flex items-center gap-2 flex-wrap ${justifyClasses[justify] || justifyClasses.between} ${className}`}
    >
      {children}
    </div>
  );
};

class ModalManager {
  constructor() {
    this.modals = [];
    this.listeners = [];
    this.escAttached = false;
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  handleKeyDown(event) {
    if (event.key !== 'Escape') return;
    const top = this.modals[this.modals.length - 1];
    if (!top) return;
    this.modals.pop();
    this.notifyListeners();
    try {
      if (typeof top.onClose === 'function') top.onClose();
    } catch (_) {
      /* ignore */
    }
    if (this.modals.length === 0) this.detachEsc();
  }

  attachEsc() {
    if (this.escAttached) return;
    document.addEventListener('keydown', this.handleKeyDown);
    this.escAttached = true;
  }

  detachEsc() {
    if (!this.escAttached) return;
    document.removeEventListener('keydown', this.handleKeyDown);
    this.escAttached = false;
  }

  register(modalId, onClose) {
    this.modals.push({ id: modalId, onClose });
    this.attachEsc();
    this.notifyListeners();
  }

  unregister(modalId) {
    this.modals = this.modals.filter((m) => m.id !== modalId);
    if (this.modals.length === 0) this.detachEsc();
    this.notifyListeners();
  }

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  notifyListeners() {
    this.listeners.forEach((listener) => listener(this.modals));
  }

  closeTop() {
    const top = this.modals[this.modals.length - 1];
    if (!top) return null;
    this.modals.pop();
    this.notifyListeners();
    try {
      if (typeof top.onClose === 'function') top.onClose();
    } catch (_) {
      /* ignore */
    }
    if (this.modals.length === 0) this.detachEsc();
    return top.id;
  }
}

export const globalModalManager = new ModalManager();

export const useModalManager = () => {
  const [modals, setModals] = React.useState([]);

  React.useEffect(() => {
    const unsubscribe = globalModalManager.subscribe(setModals);
    return unsubscribe;
  }, []);

  const openModal = (modalId, onClose) => {
    globalModalManager.register(modalId, onClose);
  };

  const closeModal = (modalId) => {
    globalModalManager.unregister(modalId);
  };

  const closeTopModal = () => globalModalManager.closeTop();

  return { modals, openModal, closeModal, closeTopModal };
};

const SIZE_CLASSES = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
  full: 'max-w-full',
};

export const StandardModal = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  className = '',
  showCloseButton = true,
  zIndex = 'z-50',
  section = MODAL_SECTIONS.DEFAULT,
}) => {
  const modalIdRef = useRef(`modal-${Math.random().toString(36).slice(2)}`);
  const sectionKey = resolveModalSection(section);

  useEffect(() => {
    if (isOpen) {
      globalModalManager.register(modalIdRef.current, onClose);
      return () => globalModalManager.unregister(modalIdRef.current);
    }
    return undefined;
  }, [isOpen, onClose]);

  return (
    <ModalOverlay
      open={isOpen}
      onClose={onClose}
      zIndexClass={zIndex}
      closeOnBackdrop
      suppressEscapeKey
      className="pt-[max(0.75rem,env(safe-area-inset-top))] pb-[max(0.75rem,env(safe-area-inset-bottom))] pl-[max(0.75rem,env(safe-area-inset-left))] pr-[max(0.75rem,env(safe-area-inset-right))]"
    >
      <ModalSectionProvider section={sectionKey}>
        <ModalPanel
          section={sectionKey}
          title={title}
          onClose={onClose}
          showCloseButton={showCloseButton}
          sizeClass={`${SIZE_CLASSES[size] || SIZE_CLASSES.md}`}
          className={className}
        >
          {children}
        </ModalPanel>
      </ModalSectionProvider>
    </ModalOverlay>
  );
};

export const PreviewModal = ({
  isOpen,
  onClose,
  title,
  children,
  showBackButton = false,
  onBack,
  showPrintButton = false,
  onPrint,
  showPdfButton = false,
  onPdf,
  showZoomControls = false,
  zoom = 1,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  maxWidth = 'max-w-4xl',
  maxHeight = 'max-h-[90vh]',
  zIndex = 'z-[9999]',
  extraActions = null,
  section = MODAL_SECTIONS.DEFAULT,
  scaleContentWithZoom = true,
}) => {
  const contentRef = useRef(null);
  const modalIdRef = useRef(`modal-${Math.random().toString(36).slice(2)}`);
  const [isMobile, setIsMobile] = useState(false);
  const sectionKey = resolveModalSection(section);
  const theme = getModalSectionTheme(sectionKey);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isOpen) {
      globalModalManager.register(modalIdRef.current, onClose);
      return () => globalModalManager.unregister(modalIdRef.current);
    }
    return undefined;
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const panelCls = isMobile
    ? `${theme.panel} rounded-none shadow-none flex flex-col h-[100dvh] max-h-[100dvh] w-full max-w-full`
    : `${theme.panel} mx-auto w-full ${maxWidth} ${maxHeight}`;

  const toolbar =
    extraActions || showPrintButton || showPdfButton || showZoomControls || isMobile ? (
      <div className={theme.toolbar}>
        {extraActions}
        {showPrintButton && (
          <StandardButton variant="ghost" onClick={onPrint} size="sm">
            Print
          </StandardButton>
        )}
        {showPdfButton && (
          <StandardButton variant="ghost" onClick={onPdf} size="sm">
            Save as PDF
          </StandardButton>
        )}
        {showZoomControls && (
          <div className={theme.zoomGroup}>
            <StandardButton variant="light" onClick={onZoomOut} size="sm" className="!min-w-[2rem]">
              −
            </StandardButton>
            <span className="min-w-[3rem] px-1 text-center text-xs font-bold text-slate-700 tabular-nums">
              {Math.round(zoom * 100)}%
            </span>
            <StandardButton variant="light" onClick={onZoomIn} size="sm" className="!min-w-[2rem]">
              +
            </StandardButton>
            {onZoomReset && (
              <StandardButton variant="light" onClick={onZoomReset} size="sm">
                Reset
              </StandardButton>
            )}
          </div>
        )}
        {isMobile && (
          <StandardButton variant="ghost" onClick={onClose} size="sm" className="min-h-[40px]">
            Close
          </StandardButton>
        )}
      </div>
    ) : null;

  return (
    <ModalOverlay
      open={isOpen}
      onClose={onClose}
      zIndexClass={zIndex}
      closeOnBackdrop
      suppressEscapeKey
      align={isMobile ? 'start' : 'center'}
      className={isMobile ? 'p-0' : 'p-2 sm:p-4'}
    >
      <ModalSectionProvider section={sectionKey}>
        <div
          data-acctoo-modal-panel="true"
          aria-labelledby="preview-modal-title"
          className={`${panelCls} min-w-0`}
          style={{ touchAction: 'manipulation' }}
        >
          <div className={theme.topStripe} aria-hidden />
          <div className={`${theme.header} shrink-0`}>
            <div className={theme.headerRow}>
              <div className="min-w-0 flex-1">
                {showBackButton && (
                  <StandardButton variant="ghost" onClick={onBack} className="mb-2" size="sm">
                    ← Back to list
                  </StandardButton>
                )}
                <h3 id="preview-modal-title" className={theme.headerTitle}>
                  {title}
                </h3>
                <p className={`${theme.headerHint} ${isMobile ? 'hidden' : ''}`}>
                  Click outside or ESC to close
                </p>
              </div>
              <ModalCloseButton
                onClose={onClose}
                label="Close preview"
                theme={theme}
                className="min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 touch-manipulation"
              />
            </div>
            {toolbar}
          </div>

          <div
            ref={contentRef}
            className={`${theme.previewBody} flex-1 min-h-0 ${isMobile ? 'p-1 sm:p-2' : 'p-3 sm:p-4'}`}
            style={{
              touchAction: 'pan-y',
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
              overflowX: scaleContentWithZoom && zoom > 1 ? 'auto' : 'hidden',
              overflowY: 'auto',
              WebkitUserSelect: 'text',
              userSelect: 'text',
            }}
          >
            {scaleContentWithZoom ? (
            <div
              className="w-full min-w-0 mx-auto"
              style={{
                transform: `scale(${zoom})`,
                transformOrigin: 'top center',
                transition: 'transform 0.2s ease-out',
                width: isMobile ? '100%' : 'auto',
                maxWidth: '100%',
              }}
            >
              {children}
            </div>
            ) : (
            <div className="w-full min-w-0 mx-auto max-w-full">
              {children}
            </div>
            )}
          </div>
        </div>
      </ModalSectionProvider>
    </ModalOverlay>
  );
};

export const ConfirmationModal = ({
  isOpen,
  onClose,
  title,
  message,
  onConfirm,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  section = MODAL_SECTIONS.DEFAULT,
  zIndex = 'z-50',
  confirmDisabled = false,
}) => (
  <StandardModal isOpen={isOpen} onClose={onClose} title={title} size="md" section={section} zIndex={zIndex}>
    <div className="text-center py-1">
      <p className="mb-6 text-sm text-slate-600 leading-relaxed">{message}</p>
      <div className="flex flex-col-reverse sm:flex-row justify-center gap-3">
        <StandardButton
          variant="secondary"
          onClick={onClose}
          disabled={confirmDisabled}
          className="w-full sm:w-auto min-h-[44px] sm:min-h-0"
        >
          {cancelText}
        </StandardButton>
        <StandardButton
          variant={variant}
          onClick={onConfirm}
          disabled={confirmDisabled}
          className="w-full sm:w-auto min-h-[44px] sm:min-h-0"
        >
          {confirmText}
        </StandardButton>
      </div>
    </div>
  </StandardModal>
);

export const FormModal = ({
  isOpen,
  onClose,
  title,
  children,
  onSubmit,
  submitText = 'Submit',
  cancelText = 'Cancel',
  submitVariant = 'primary',
  size = 'md',
  className = '',
  showClearButton = false,
  clearButtonText = 'Clear Form',
  onClear,
  section = MODAL_SECTIONS.DEFAULT,
}) => {
  const theme = getModalSectionTheme(resolveModalSection(section));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSubmit) onSubmit(e);
  };

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size={size}
      className={className}
      section={section}
    >
      <form onSubmit={handleSubmit} className="flex flex-col min-h-0">
        <div className="mb-1">{children}</div>
        <div className={theme.formFooter}>
          <StandardButton
            variant="secondary"
            onClick={onClose}
            type="button"
            className="w-full sm:w-auto min-h-[44px] sm:min-h-0"
          >
            {cancelText}
          </StandardButton>
          {showClearButton && onClear && (
            <StandardButton
              variant="warning"
              onClick={onClear}
              type="button"
              className="w-full sm:w-auto min-h-[44px] sm:min-h-0"
            >
              {clearButtonText}
            </StandardButton>
          )}
          <StandardButton
            variant={submitVariant}
            type="submit"
            className="w-full sm:w-auto min-h-[44px] sm:min-h-0"
          >
            {submitText}
          </StandardButton>
        </div>
      </form>
    </StandardModal>
  );
};
